import { useRef, useEffect, useState } from "react";

import './letters.css';

const PRESETS = {
  figure8: {
    G: 1,
    bodies: [
      {
        mass: 1,
        position: { x: -0.97000436, y: 0.24308753 },
        velocity: { x: 0.466203685, y: 0.43236573 },
        color: "#ff5555",
        radius: 4,
      },
      {
        mass: 1,
        position: { x: 0.97000436, y: -0.24308753 },
        velocity: { x: 0.466203685, y: 0.43236573 },
        color: "#55ff55",
        radius: 4,
      },
      {
        mass: 1,
        position: { x: 0, y: 0 },
        velocity: { x: -0.93240737, y: -0.86473146 },
        color: "#5555ff",
        radius: 4,
      },
    ],
  },

  lagrange: {
  G: 10,
  bodies: (() => {
    const m = 10;
    const L = 200;
    const R = L / Math.sqrt(3);

    const omega = Math.sqrt((1 * m) / (R * R * R));
    const v = omega * R;

    return [
      {
        mass: m,
        position: { x: 0, y: -R },
        velocity: { x: v, y: 0 },
        color: "#ffcc00",
        radius: 6,
      },
      {
        mass: m,
        position: {
          x: (Math.sqrt(3) / 2) * R,
          y: R / 2,
        },
        velocity: {
          x: -v / 2,
          y: (Math.sqrt(3) / 2) * v,
        },
        color: "#ff8800",
        radius: 6,
      },
      {
        mass: m,
        position: {
          x: -(Math.sqrt(3) / 2) * R,
          y: R / 2,
        },
        velocity: {
          x: -v / 2,
          y: -(Math.sqrt(3) / 2) * v,
        },
        color: "#ffaa00",
        radius: 6,
      },
    ];
  })(),
  },

  burrau: {
    G: 1,
    bodies: [
      {
        mass: 3,
        position: { x: -150, y: 0 },
        velocity: { x: 0, y: 0 },
        color: "#ff4444",
        radius: 6,
      },
      {
        mass: 4,
        position: { x: 150, y: 0 },
        velocity: { x: 0, y: 0 },
        color: "#44ff44",
        radius: 6,
      },
      {
        mass: 5,
        position: { x: 0, y: 200 },
        velocity: { x: 0, y: 0 },
        color: "#4444ff",
        radius: 6,
      },
    ],
  },

  hierarchical: {
    G: 1,
    bodies: (() => {
      const m = 10;
      const r = 20;           // tight binary
      const R = 300;          // far third body

      // inner binary circular velocity
      const vInner = Math.sqrt((1 * m) / (4 * r));

      // outer orbit velocity around binary COM
      const vOuter = Math.sqrt((1 * (2 * m)) / R);

      return [
        // Binary body A
        {
          mass: m,
          position: { x: -r, y: 0 },
          velocity: { x: 0, y: -vInner },
          color: "#ff7777",
          radius: 6,
        },

        // Binary body B
        {
          mass: m,
          position: { x: r, y: 0 },
          velocity: { x: 0, y: vInner },
          color: "#77ff77",
          radius: 6,
        },

        // Outer perturber
        {
          mass: 5,
          position: { x: 0, y: R },
          velocity: { x: vOuter, y: 0 },
          color: "#7777ff",
          radius: 5,
        },
      ];
    })()
  },

  sunPlanetMoon: {
    G: 0.981,
    bodies: [
      {
        mass: 5000,
        position: { x: 0, y: 0 },
        velocity: { x: 0, y: 0 },
        color: "#ffaa00",
        radius: 20,
      },
      {
        mass: 50,
        position: { x: 250, y: 0 },
        velocity: { x: 0, y: 1.9 },
        color: "#55aaff",
        radius: 6,
      },
      {
        mass: 0.1,
        position: { x: 280, y: 0 },
        velocity: { x: 0, y: 1.9 + Math.sqrt((0.3 * 50) / 30)},
        color: "#dddddd",
        radius: 3,
      },
    ],
  },
};

export default function ThreeBodySimulation({
  preset = "figure8",
  timeStep = 1,
  trailLength = 800,
  background = "black",
  showEnergyNumbers = true,
  momentumScale = 20,
}) {
  const canvasRef = useRef(null);
  const energyHistoryRef = useRef([]);

  const [showMomentumVectors, setShowMomentumVectors] = useState(false);

  const { bodies, G } = PRESETS[preset];

  const stateRef = useRef(
    bodies.map((b) => ({
      ...b,
      position: { ...b.position },
      velocity: { ...b.velocity },
      trail: [],
    }))
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const step = () => {
      const state = stateRef.current;

      const acc = state.map(() => ({ x: 0, y: 0 }));
      let kinetic = 0;
      let potential = 0;

      // Physics + energy
      for (let i = 0; i < state.length; i++) {
        for (let j = i + 1; j < state.length; j++) {
          const bi = state[i];
          const bj = state[j];

          const dx = bj.position.x - bi.position.x;
          const dy = bj.position.y - bi.position.y;
          const r2 = dx * dx + dy * dy + 0.0001;
          const r = Math.sqrt(r2);

          // Acceleration magnitudes
          const ai = (G * bj.mass) / r2;
          const aj = (G * bi.mass) / r2;

          // Direction
          const ux = dx / r;
          const uy = dy / r;

          // Apply accelerations (Newton 3rd law)
          acc[i].x += ai * ux;
          acc[i].y += ai * uy;

          acc[j].x -= aj * ux;
          acc[j].y -= aj * uy;

          // Potential energy
          potential -= (G * bi.mass * bj.mass) / r;
        }
      }


      const totalEnergy = kinetic + potential;

      energyHistoryRef.current.push({
        kinetic,
        potential,
        total: totalEnergy,
      });
      if (energyHistoryRef.current.length > 300)
        energyHistoryRef.current.shift();

      // Integrate
      state.forEach((b, i) => {
        b.velocity.x += acc[i].x * timeStep;
        b.velocity.y += acc[i].y * timeStep;
        b.position.x += b.velocity.x * timeStep;
        b.position.y += b.velocity.y * timeStep;

        b.trail.push({ ...b.position });
        if (b.trail.length > trailLength) b.trail.shift();
      });

      // Clear
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);

      // Trails
      state.forEach((b) => {
        ctx.strokeStyle = b.color;
        ctx.beginPath();
        b.trail.forEach((p, i) =>
          i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)
        );
        ctx.stroke();
      });

      // Bodies
      state.forEach((b) => {
        ctx.fillStyle = b.color;
        ctx.beginPath();
        ctx.arc(b.position.x, b.position.y, b.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      // Momentum vectors (optional)
      if (showMomentumVectors) {
        state.forEach((b) => {
          const px = b.mass * b.velocity.x * momentumScale;
          const py = b.mass * b.velocity.y * momentumScale;

          ctx.strokeStyle = b.color;
          ctx.globalAlpha = 0.7;
          ctx.beginPath();
          ctx.moveTo(b.position.x, b.position.y);
          ctx.lineTo(
            b.position.x + px,
            b.position.y + py
          );
          ctx.stroke();
          ctx.globalAlpha = 1;
        });
      }

      ctx.restore();

      /* ---- ENERGY GRAPH ---- */
      const graphX = 20;
      const graphY = canvas.height - 140;
      const graphW = 260;
      const graphH = 100;

      ctx.strokeStyle = "#444";
      ctx.strokeRect(graphX, graphY, graphW, graphH);

      const energies = energyHistoryRef.current;
      const maxE =
        Math.max(...energies.map((e) => Math.abs(e.total))) || 1;

      const drawEnergy = (key, color) => {
        ctx.strokeStyle = color;
        ctx.beginPath();
        energies.forEach((e, i) => {
          const x = graphX + (i / energies.length) * graphW;
          const y =
            graphY +
            graphH / 2 -
            (e[key] / maxE) * (graphH / 2);
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        });
        ctx.stroke();
      };

      drawEnergy("kinetic", "#ffaa00");
      drawEnergy("potential", "#55aaff");
      drawEnergy("total", "#ffffff");

      /* ---- ENERGY NUMBERS (optional) ---- */
      if (showEnergyNumbers) {
        ctx.fillStyle = "#fff";
        ctx.font = "12px monospace";
        ctx.textAlign = "left";

        ctx.fillText(
          `K: ${kinetic.toFixed(3)}`,
          graphX,
          graphY - 28
        );
        ctx.fillText(
          `U: ${potential.toFixed(3)}`,
          graphX,
          graphY - 14
        );
        ctx.fillText(
          `E: ${totalEnergy.toFixed(3)}`,
          graphX + 130,
          graphY - 14
        );
      }

      requestAnimationFrame(step);
    };

    step();
    return () => window.removeEventListener("resize", resize);
  }, [
    preset,
    timeStep,
    trailLength,
    background,
    showMomentumVectors,
    showEnergyNumbers,
    momentumScale,
  ]);

  useEffect(() => {
    function handleClickCanvas(e) { setShowMomentumVectors(s => !s); }

    window.addEventListener("click", handleClickCanvas);
    return () => window.removeEventListener("click", handleClickCanvas);
  }, [setShowMomentumVectors]);

  return (
    <div className="letter-grid">
      <canvas
        ref={canvasRef}
        style={{
          width: "100vw",
          height: "100vh",
          display: "block",
        }}
      />
    </div>
  );
}
